import * as Highcharts from "highcharts";
import * as $ from "jquery";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import "select2";
import { TimezoneCollection } from "../../../../collections/Timezones";
import { Utils } from "../../../../common/utils";
import { WIDGET_DIMENSIONS } from "../../../../common/widgetLayout";
import { BaseDashboardWidgetView, BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import {
    CHART_AGGREGATION_PERIOD,
    CHART_AGGREGATION_TYPE,
    CHART_CONNECT_TO_TILES,
    CHART_CONVERT_ALL_DATA,
    CHART_DATA_RANGE,
    CHART_DATA_ZOOM,
    CHART_HIDE_EXPORTS,
    CHART_HIDE_SINGLE_GAPS,
    CHART_IS_TALL,
    CHART_LABELS,
    CHART_REAL_TIMESTAMPS,
    CHART_SIZE,
    CHART_THRESHOLDS,
    CHART_TYPE,
    ChartConfig,
    ChartConfigSeries,
    ChartConfigSeriesGroup,
    ChartHelper,
    CustomChartConfigSeries,
    DAILY_TIME_MODE,
    minMaxTimeStampOptions
} from "./ChartHelper";

import { MenuItem } from "highcharts";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { DashboardRange } from "../../../../models/DashboardRange";
import { Install } from "../../../../models/Install";
import { GetSeriesOptions } from "../ChartWorkerManager";
import SeriesObject = Highcharts.SeriesObject;
import PlotBand = Highcharts.PlotBands;

require('bootstrap-datetimepicker');

declare var App: any;



// The chart view
export class ChartView extends BaseDashboardWidgetView {

    // Determines regular or advanced thresholds
    protected advancedConfig = false;
    // Determines default or custom labels
    protected useCustomLabels = true;
    // Reference to the Highcharts container object
    protected chart: any = null;
    // The current configuration of the chart
    protected chartConfig: ChartConfig = null;
    // Index of the zoom button that was last pressed
    protected lastZoomLevel: string = null;
    // Which channels are initially selected on the graph
    protected initialChannelIds: number[] = [];

    /* Maps an index (e.g. a dashboard channel id) to a Highcharts Series */
    protected seriesMap: { [key: string]: SeriesObject };
    /* Maps a unit (e.g. °F) to an array of dashboard channel ids that use the axis */
    protected axisMap: { [key: string]: number[] };
    /* Maps an index (e.g. a dashboard channel id) to a workerId */
    protected workers: { [key: string]: string };

    /* Which channels are currently loading */
    protected channelsLoading: number[];
    /* Pending zoom level updates */
    protected pendingGraphExtremes: number = null;
    /* What's the earliest/latest we've ever requested data from */
    protected minDataRequested: number = Infinity;
    protected maxDataRequested: number = null;

    /* Which channels are currently graphed */
    protected graphedDashboardChannelIds: number[];

    protected filterChangeTimeout: any = null;
    protected initTime: number;
    protected fetchAllDataPromise = null;

    protected lastFilterValue: string = null;
    protected chartButtons: any[] = [];
    protected $chartContainer: JQuery;

    protected template = _.template(require('templates/dashboard/widgets/chart/chartView.html'));

    protected dateTimePickerFormat = 'MM/DD/YYYY h:mm A';
    protected previousTimePickerInputValid = false;

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(<any>_.extend({
            tagName: "div",
            className: 'widget',
            events: {
                // Change Chart Types
                'change .w-chart .chart-type': 'handleChangeChartType',
                'change .w-chart .chart-period': 'handleChangeChartType',
                'change .w-chart .chart-stat': 'handleChangeChartType',
                'click .w-chart .chart-label span': 'handleChangeChartType',
                'click .w-chart .chart-showthreshold span': 'handleChangeChartType',
                'click .w-chart .chart-timestamps span': 'handleChangeChartType',
                'click .w-chart .chart-convert span': 'handleChangeChartType',
                'input .chart-data-range input': 'handleTimePickerChange',
                'dp.change .chart-data-range .date': 'handleTimePickerChange',
                'click .chart-data-range .reset': 'handleTimeSelectorReset',
                'click .chart-toolbar-right .clear-all-selections': 'handleClearAllSelections',

                // Widget related buttons
                'click .w-settings .remove': 'handleRemoveWidgetClick',
                'click .w-delete-widget .w-cancel': 'handleRemoveWidgetCancelClick',
                'click .w-delete-widget .w-confirm': 'handleRemoveWidgetConfirmClick',
                'click .w-settings .gear': 'handleEditWidgetClick',
                'click .w-settings .clone': 'handleCloneWidget'

            }
        }, options));

        const rawAdvancedConfig = this.options.widget.getParamValue('ADVANCED_CONFIG', false);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.advancedConfig = !!Number(rawAdvancedConfig);

        const rawUseCustomLabelsConfig = this.options.widget.getParamValue('USE_CUSTOM_LABELS', true);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomLabels = !!Number(rawUseCustomLabelsConfig);

        this.reset();
        this.resetChartConfig();

        /* Keep track of the time this view was
         * initialized, since a node may suddenly
         * upload old data while the dashboard is open, so we always load data from when the
         * page was last refreshed */
        this.initTime = Math.floor((new Date()).getTime() / 1000);
        this.recalculatePlotlines = _.debounce(this.recalculatePlotlines, 1000);
        this.scheduleRedraw = _.debounce(this.scheduleRedraw.bind(this), 100);
    }

    protected destroy() {
        _.each(_.values(this.workers), (wId) => {
            this.options.chartWorkerManager.deRegisterWorker(wId);
        });

        this.seriesMap = {};
        this.workers = {};
        this.axisMap = {};

        this.channelsLoading = [];
        this.pendingGraphExtremes = null;
        this.minDataRequested = Infinity;
        this.maxDataRequested = null;
        this.graphedDashboardChannelIds = [];

        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    protected reset() {
        var chartIsRendered = Boolean(this.chart);
        this.destroy();
        if (chartIsRendered) this.renderPlot();
    }

    protected resetChartConfig() {
        var App = window['App'];

        this.chartConfig = {
            seriesGroups: [],
            timezone: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser),
            showThresholdPlotlines: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_THRESHOLDS', CHART_THRESHOLDS.DEFAULT)),
            realTimestamps: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_TIMESTAMPS', CHART_REAL_TIMESTAMPS.DEFAULT)),
            hideSingleGaps: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_HIDE_SINGLE_GAPS', CHART_HIDE_SINGLE_GAPS.DEFAULT)),
            convertAllValues: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_CONVERT_ALL_DATA', CHART_CONVERT_ALL_DATA.DEFAULT)),
            labels: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_LABELS', CHART_LABELS.DEFAULT))
        };

        this.generateSeriesGroupConfigs();

        if (_.some(this.chartConfig.seriesGroups, (group) => {
            return group.type == CHART_TYPE.STACKEDAREA;
        })) {
            this.chartConfig.realTimestamps = false;
        }

        // Remove all existing app listeners
        this.stopListening(App.Bus);

        // Should we connect this graph to widgets in the same category?
        var connectToWidgets = this.options.widget.getParamValue('CHART_CONNECT_TO_TILES', CHART_CONNECT_TO_TILES.CONNECT_TO_ALL);

        // Set up listeners for simple widgets
        if (connectToWidgets === CHART_CONNECT_TO_TILES.CONNECT_TO_ALL) {
            // Capture Events across all categories
            this.listenTo(App.Bus, 'dashboard:category:all:chart:removeChannel', this.removeChannel);
            this.listenTo(App.Bus, 'dashboard:category:all:chart:addChannel', this.addChannel);
        } else if (connectToWidgets === CHART_CONNECT_TO_TILES.ENABLED) {
            // Capture Events in this Category
            this.listenTo(App.Bus, 'dashboard:category:' + this.options.categoryId + ':chart:removeChannel', this.removeChannel);
            this.listenTo(App.Bus, 'dashboard:category:' + this.options.categoryId + ':chart:addChannel', this.addChannel);
        }
        

        // Set up listener for widgets to query which channels are in the chart
        // Only for default chart (which connects to all widgets)
        if (this.isDefaultChart()) {
            App.Bus.on('dashboard:category:all:chart:getChannels', this.onGetChannelsRequest, this);
        }

        // If this is not a default chart, it will respond to dashboard filters based on the name of the series
        if (!this.isDefaultChart()) {
            this.lastFilterValue = null;
            this.listenTo(App.Bus, 'dashboard:filter:notify', (filtervalue) => {
                this.lastFilterValue = filtervalue;

                this.handleFilterChange();
            });
        }
    }

    protected generateSeriesGroupConfigs(dashboardChannelIdsToGraph?: number[]) {
        if (!dashboardChannelIdsToGraph) {
            dashboardChannelIdsToGraph = [];
            var widgetId = this.options.widget.get('id');
            this.options.dashboardChannelCollection.each((c) => {
                if (c.get('dashboard_widget_id') === widgetId) {
                    dashboardChannelIdsToGraph.push(c.get('dashboard_channel_id'))
                }
            });
        }

        _.each(dashboardChannelIdsToGraph, (id) => {
            this.addToSeriesGroups(id);
        });
    }

    protected addToSeriesGroups(dashboardChannelId: number) {
        var chartTypes, aggregationTypes, period, showUnit;

        // Generate the config from widget params or from the ui?
        if (this.isDefaultChart()) {
            chartTypes = [this.getValueFromUIOrDefault('.chart-type', null, CHART_TYPE.DEFAULT)];
            aggregationTypes = [this.getValueFromUIOrDefault('.chart-stat', null, CHART_AGGREGATION_TYPE.DEFAULT)];
            period = this.getValueFromUIOrDefault('.chart-period', null, CHART_AGGREGATION_PERIOD.DEFAULT);
            showUnit = true;
        } else {
            var typeParam = this.options.widget.getParamValue('CHART_TYPE', CHART_TYPE.DEFAULT);
            chartTypes = _.map(_.split(typeParam, ','));

            var aggregationParam = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', CHART_AGGREGATION_TYPE.DEFAULT);
            aggregationTypes = _.map(_.split(aggregationParam, ','));

            period = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT);
            showUnit = ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_CONVERT_ALL_DATA', CHART_CONVERT_ALL_DATA.DEFAULT));
        }

        var channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(dashboardChannelId) });
        var secondary_order = channel.get('dashboard_channel_secondary_order');
        var order = channel.get('dashboard_channel_order');
        var decimals = channel.get('channel_formatted_decimals') || 2;
        var suffix = showUnit ? channel.get('channel_unit') : '';
        var channelId = channel.get('channel_id');

        // Use default name or custom name based on configuration
        let dashboardChannelName = channel.get('dashboard_channel_name');
        const moduleName = channel.get('module_location');
        if(this.isDefaultChart()){
            // Add module name if it's nbot already included in the name
            if (!dashboardChannelName.includes(moduleName)) {
                dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`
            }
        } else if(!this.useCustomLabels){
            dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`;
        }

        // Default to the first type/period if there is
        // not the right number of periods in the array
        var aggType, type;
        if (period === CHART_AGGREGATION_PERIOD.NONE) {
            aggType = CHART_AGGREGATION_TYPE.NONE;
        } else if (order && order < aggregationTypes.length) {
            aggType = aggregationTypes[order];
        } else {
            aggType = aggregationTypes[0];
        }
        if (secondary_order && secondary_order < chartTypes.length) {
            type = chartTypes[secondary_order];
        } else {
            type = chartTypes[0];
        }

        var series = {
            name: _.escape(dashboardChannelName),
            valueSuffix: _.escape(suffix),
            valueDecimals: decimals,
            agg: _.escape(aggType),
            tag: dashboardChannelId,
            channelId: channelId,
        };
        var gIndex = _.findIndex(this.chartConfig.seriesGroups, (<any>{ period: period, type: type }));

        if (gIndex >= 0) {
            this.chartConfig.seriesGroups[gIndex].series.push(series)
        } else {
            this.chartConfig.seriesGroups.push({
                period: period,
                type: type,
                series: [series],
            })
        }
    }

    protected removeFromSeriesGroups(dashboardChannelId) {
        _.each(this.chartConfig.seriesGroups, (group, index) => {
            this.chartConfig.seriesGroups[index].series = _.filter(group.series, (series) => {
                return series.tag !== dashboardChannelId;
            })
        });
        this.chartConfig.seriesGroups = _.filter(this.chartConfig.seriesGroups, (group) => {
            return group.series.length > 0;
        })
    }

    protected getGroupFromSeriesTag(tag: number): ChartConfigSeriesGroup {
        var group = _.find(this.chartConfig.seriesGroups, (group) => {
            return _.some(group.series, (seriesInfo) => {
                return seriesInfo.tag == tag;
            })
        });
        if (!group) return null;
        return group;
    }

    protected getSeriesFromTag(tag: number): ChartConfigSeries {
        var series;
        _.each(this.chartConfig.seriesGroups, (group) => {
            _.each(group.series, (seriesInfo) => {
                if (seriesInfo.tag == tag) {
                    series = seriesInfo;
                    return;
                }
            });
            if (!_.isEmpty(series)) return;
        });
        return series;
    }

    protected requestSelectedChannels() {
        // After a chart has change we must ask all widgets to resubmit their state, so that we can track it
        var category = this.isDefaultChart() ? 'all' : this.options.categoryId;
        window['App'].Bus.trigger('dashboard:category:' + category + ':widgets:getStateFromRange');
    }

    protected getPollingFrequencyForSystemStatus() {
        if (this.options.dashboardChannelCollection.length) {
            return this.options.dashboardChannelCollection.first().get('module_polling_frequency')
        }
    }

    protected addChannel(dashboardChannelId: number) {
        if (_.includes(this.graphedDashboardChannelIds, dashboardChannelId)) return;

        this.graphedDashboardChannelIds.push(dashboardChannelId);
        this.channelsLoading.push(dashboardChannelId);

        this.setChartExportName(this.graphedDashboardChannelIds);
        var chartConfigGroup = this.getGroupFromSeriesTag(dashboardChannelId);
        // When channel is added with the default chart by clicking a tile
        // We need to add a new series entry based on generic settings
        if (!chartConfigGroup) {
            this.addToSeriesGroups(dashboardChannelId);
            chartConfigGroup = this.getGroupFromSeriesTag(dashboardChannelId);
        }
        var chartConfigSeries = this.getSeriesFromTag(dashboardChannelId);
        var chartType = chartConfigGroup.type;
        var isHeatmap = chartType === CHART_TYPE.HEATMAP;
        var isPie = chartType === CHART_TYPE.PIE;

        // Find the channel
        var channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(dashboardChannelId) });

        // Make sure to remove all yAxis before loading the correct one for gauges
        if (chartType === CHART_TYPE.GAUGE) {
            _.each(this.chart.yAxis, (yAxis) => {
                yAxis.remove();
            });
        }
        // Create a new axis if none exists for the channel's unit
        var axisKey = this.getAxisKeyForChannel(channel);
        if (!isHeatmap && !this.axisMap[axisKey]) {
            var yAxisConfig = this.getYAxisConfig(chartConfigGroup, chartConfigSeries);
            yAxisConfig['id'] = axisKey;
            this.chart.addAxis(yAxisConfig);
            this.axisMap[axisKey] = [];
        }

        // Create the new Series Configuration
        var seriesConfig = {
            marker: { enabled: false, states: { hover: { enabled: true } } },
            connectNulls: false,
            type: ChartHelper.getHighchartsTypeFromChartType(chartConfigGroup.type),
            rawType: chartConfigGroup.type,
            tooltip: {
                valueDecimals: chartConfigSeries.valueDecimals,
                valueSuffix: this.chartConfig.convertAllValues ? chartConfigSeries.valueSuffix : ''
            },
            name: chartConfigSeries.name,
            showInNavigator: true,
        };

        // If it's not a default chart (e.g. widget), use the order to determine the color
        const isDefaultChart = this.isDefaultChart();
        if(!isDefaultChart){
            const widgetChannels = this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widget.get('id') });
            const index = widgetChannels.findIndex(c => c.get('dashboard_channel_id') === Number(dashboardChannelId));
            seriesConfig['index'] = index;
            seriesConfig['color'] = this.getColorByOrder(index);
        }

        // Unset the highcharts default for data exporting
        Highcharts.setOptions({
            global: {
                useUTC: false,
            }
        });

        if (chartType === CHART_TYPE.STACKEDAREA || chartType === CHART_TYPE.STACKEDCOLUMN) {
            seriesConfig['stacking'] = 'normal';
        } else if (chartType !== CHART_TYPE.PIE) {
            seriesConfig['dataGrouping'] = {
                approximation: ChartHelper.getHighChartsApproximationFromAggregationType(chartConfigSeries.agg)
            }
        }
        if (chartType === CHART_TYPE.STACKEDAREA) {
            seriesConfig['states'] = {
                hover: false,
            }
        }

        if (chartType === CHART_TYPE.GAUGE) {
            seriesConfig['overshoot'] = 5;
        }

        // Associate the series with an axis. For heatmaps there is only one yAxis
        if (!isHeatmap && !isPie) {
            seriesConfig['yAxis'] = axisKey;

            // For stacked bar charts make sure series are stacked by unit
            seriesConfig['stack'] = axisKey;

            this.axisMap[axisKey].push(dashboardChannelId);
        }

        // Create the new Series
        if (isHeatmap || isPie) {
            var index = chartConfigGroup.type;
        } else {
            var index = String(dashboardChannelId);
        }

        if (!this.seriesMap[index]) {

            var customHighcharts: CustomChartConfigSeries = JSON.parse(this.options.widget.getParamValue('CHART_HIGHCHARTS', '{}'));
            var customSeries = customHighcharts.series ? customHighcharts.series[index] : {};

            //Override values from the stored widget param for this 'index'
            this.seriesMap[index] = this.chart.addSeries(_.extend(seriesConfig, customSeries));
        }

        if (isHeatmap || isPie) {
            // Always get a new worker for heatmaps and pie charts in order to avoid making duplicate requests
            if (this.workers[index]) {
                this.options.chartWorkerManager.deRegisterWorker(this.workers[index]);
            }
        }
        this.workers[index] = this.options.chartWorkerManager.registerChannelWorker();

        // Figure out when to load data from
        var min, max;
        var initial = this.minDataRequested === Infinity;
        var baseMinMaxOptions = {
            initial: initial,
            channel: channel,
            widget: this.options.widget,
            customDataRangeFunc: this.getCustomDataRangeInput.bind(this),
            isDefaultChart: this.isDefaultChart(),
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            graphedDashboardChannelIds: this.graphedDashboardChannelIds,
            user: this.options.currentUser
        };
        var maxOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
            maxDataRequested: this.maxDataRequested
        });
        max = ChartHelper.getMaxTimestampToRequest(maxOptions);
        var minOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
            minDataRequested: this.minDataRequested,
            maxDataRequested: max
        });

        min = ChartHelper.getMinTimestampToRequest(minOptions);
        if(this.isDefaultChart()){
            if(min > this.minDataRequested) min = this.minDataRequested;
        }

        this.recalculatePlotlines();

        // Load the data
        this.showLoading();
        var deferred = this.fetchDataForIndex(index, min, max);
        if (initial) {
            deferred = $.when(deferred).then(() => {
                var range = ChartHelper.getChartDataRange(this.options.widget, this.options.currentUser, this.getCustomDataRangeInput.bind(this), this.isDefaultChart());
                var zoom = isPie ? range.range : ChartHelper.effectiveZoom(
                    this.options.widget.getParamValue('CHART_DATA_ZOOM', CHART_DATA_ZOOM.DEFAULT),
                    range.range
                );
                this.handleChartZoomButtonClick(zoom);
            });
        }
        return deferred;
    }

    protected onGetChannelsRequest(callback: (channelIds: number[]) => void) {
        // Return the list of channels currently in the chart
        // Call synchronously to avoid delays
        if (callback && typeof callback === 'function') {
            callback(this.graphedDashboardChannelIds || []);
        }
    }

    protected removeChannel(dashboardChannelId) {
        if (!_.includes(this.graphedDashboardChannelIds, dashboardChannelId)) return;

        var chartConfigGroup = this.getGroupFromSeriesTag(dashboardChannelId);

        this.graphedDashboardChannelIds = _.without(this.graphedDashboardChannelIds, dashboardChannelId);

        this.setChartExportName(this.graphedDashboardChannelIds);

        if (chartConfigGroup.type !== CHART_TYPE.HEATMAP && chartConfigGroup.type !== CHART_TYPE.PIE) {
            this.seriesMap[dashboardChannelId].remove();
            this.options.chartWorkerManager.deRegisterWorker(this.workers[dashboardChannelId]);

            delete this.seriesMap[dashboardChannelId];
            delete this.workers[dashboardChannelId];

            this.removeAxisForChannel(dashboardChannelId);
        } else if (this.graphedDashboardChannelIds.length) {
            // We must refetch/rerender maps if the channels change
            this.fetchAllData(this.initTime);
        }

        // If we removed a channel that was currently loading we must rerender the plot
        if (_.includes(this.channelsLoading, dashboardChannelId)) {
            this.channelsLoading = _.without(this.channelsLoading, dashboardChannelId);
            this.renderPlot();
        }

        if (!this.graphedDashboardChannelIds.length) this.reset();
        this.removeFromSeriesGroups(dashboardChannelId);
        this.recalculatePlotlines();
    }

    protected removeAxisForChannel(dashboardChannelId) {
        var chartConfigGroup = this.getGroupFromSeriesTag(dashboardChannelId);

        if (chartConfigGroup.type === CHART_TYPE.HEATMAP || chartConfigGroup.type === CHART_TYPE.PIE) return;

        // Find the yAxis this channel was associated with and if it was the only channel associated
        // with this axis, remove the axis
        // Note: we may not have the channel, because the channel was removed from the dashboard. In that case
        // we must find the axis that we do have and remove the difference
        var channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(dashboardChannelId) });
        var axisKeys;
        if (channel) {
            axisKeys = [this.getAxisKeyForChannel(channel)];
        } else {
            var remainingAxisKeys = _.uniq(_.map(
                _.map(this.graphedDashboardChannelIds, (id) => {
                    return this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(id) })
                }), (c) => {
                    this.getAxisKeyForChannel(c)
                }));
            axisKeys = _.difference((<any[]>_.keys(this.axisMap)), remainingAxisKeys);
        }

        // Keep track of a special highcharts property. if we don't restore this property at the end of the
        // delete, highcharts thinks there are no other axes and aligns the next axis that is added incorrectly
        var highchartsLabelPanesPreDelete = this.chart._labelPanes;

        _.each(axisKeys, (axisKey) => {
            // Remove Channel from our internal map
            this.axisMap[axisKey] = _.without(this.axisMap[axisKey], dashboardChannelId);

            // Remove Axis From Highcharts if this was the only channel assocuated with this axis
            if (!this.axisMap[axisKey].length) {
                for (var i = 0; i < this.chart.axes.length; i++) {
                    if (this.chart.axes[i].userOptions.id === axisKey) {
                        this.chart.axes[i].remove();
                        break;
                    }
                }
                delete this.axisMap[axisKey];
            }
        });

        // Restore the special highcharts property if this is not the only axis left
        if (_.values(this.axisMap).length) {
            this.chart._labelPanes = highchartsLabelPanesPreDelete;
        }
    }

    protected getAxisKeyForChannel(dashboardChannel) {
        var unit = dashboardChannel.get('channel_unit');
        return 'axis-' + unit;
    }

    protected recalculatePlotlines() {
        if (!this.chart || !this.chart.yAxis || this.chart.series.length == 0 || this.chart.series[0].userOptions.rawType === CHART_TYPE.GAUGE) return;

        for (var i = 0; i < this.chart.yAxis.length; i++) {
            while (this.chart.yAxis[i].plotLinesAndBands.length) {
                this.chart.yAxis[i].plotLinesAndBands[0].destroy();
            }
        }
        // Plotlines only make sense if there are line series in the chart
        if (!this.chartConfig.showThresholdPlotlines || _.every(this.chartConfig.seriesGroups, (group) => {
            return group.type !== CHART_TYPE.LINE && group.type !== CHART_TYPE.BOX_PLOT
        })) return;

        const thresholdsByAxisKey: Map<string, any> = new Map();
        _.each(this.graphedDashboardChannelIds, (id) => {
            var channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
            var axisKey = this.getAxisKeyForChannel(channel);

            // Populate thresholdsByAxisKey with ranges
            // First make sure the ranges are sorted by lowest value to highest
            // We can use either min or max value, if min is used null (infinity) should 
            // be the first order wise, if max is used it should be the last
            const rangesList = channel.get('rangesList') ?? [];
            rangesList.sort((a, b) => {
                if (a.min === null) return -1; // 'a' comes before 'b' if 'a' has null min value
                if (b.min === null) return 1; // 'b' comes before 'a' if 'b' has null min value
                return a.min - b.min; // Sort normally for numeric min values
            });
            var useMin = false;
            for (const r of rangesList) {
                var range = new DashboardRange(r);
                var value;
                if (range.get('is_default')) {
                    useMin = true;
                    continue;
                }
                useMin ? value = range.get('min') : value = range.get('max');

                if (!thresholdsByAxisKey.get(axisKey)) thresholdsByAxisKey.set(axisKey, []);

                // Use default name or custom name based on configuration
                let dashboardChannelName = channel.get('dashboard_channel_name');
                const moduleName = channel.get('module_location');
                if(this.isDefaultChart()){
                    // Add module name if it's not already included in the name
                    if (!dashboardChannelName.includes(moduleName)) {
                        dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`
                    }
                } else if(!this.useCustomLabels){
                    dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`;
                }
                
                thresholdsByAxisKey.get(axisKey).push({
                    key: range.get('name'),
                    value: value,
                    text: dashboardChannelName,
                    thresholdType: 'range',
                    type: range.get('name'),
                    color: range.get('color'),
                    alignment: useMin ? 'high' : 'low'
                });
            }
        });

        thresholdsByAxisKey.forEach((thresholds, axisKey) => {
            var axis = this.chart.get(axisKey);

            // All thresholds with the same value should share the same plotline
            var groups = _.groupBy(thresholds, 'value');
            _.each(groups, (group, value) => {
                // Alignement
                var alignments = _.uniq(_.map(group, 'alignment'));
                var offset = _.includes(alignments, 'high') ? -5 : 15;

                // Name
                var names = _.uniq(_.map(group, 'text'));
                var types = _.uniq(_.map(group, 'type'));
                var text = (names.length === 1 ? names[0] + ' - ' : '') + types.join("/");

                // Color
                var color = Utils.colors.grey;
                if (group.length == 1) {
                    var t = _.head(group);
                    color = t.color;
                } else {
                    // Get the most used color among ranges in group,
                    // else if there are no shared colors use default grey
                    var rangesByColor = _.groupBy(group, (item) => {
                        return item.color.toLowerCase();
                    });
                    var rangeCounter = 1;
                    _.each(rangesByColor, (ranges, rangesColor) => {
                        if (ranges.length > rangeCounter) {
                            color = rangesColor;
                            rangeCounter = ranges.length;
                        }
                    });
                }

                axis.addPlotLine({
                    value: Number(value),
                    color: _.escape(color),
                    dashStyle: 'shortdash',
                    width: 2,
                    zIndex: 5,
                    label: {
                        text: _.escape(text),
                        style: { fill: 'rgb(70, 70, 70)' },
                        y: offset
                    }
                });
            });
        });

        // Thresholds plotbands
        // this.addPlotBandsFromThresholds(widgetChannels);

        // Add operating hours as Plotbands to X axis
        const widgetChannels = [];
        for (const id of this.graphedDashboardChannelIds) {
            const channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
            widgetChannels.push(channel);
        }
        //  Extract unique install_id values from channelsCollection
        const installIds = [...new Set(
            widgetChannels
                .map(channel => channel.get('install_id'))
                .filter(value => value !== undefined && value !== null && value !== '')
                .filter(value => typeof value === 'string' || typeof value === 'number')
        )];
        if(installIds.length != 0) {
            // Fetch entries from installCollection that match the extracted install_id values
            const installCollection = this.options.manager?.options?.installCollection;
            if(installCollection){
                const installs = installCollection.filter(
                    install => installIds.includes(install.get('id'))
                );
                this.addPlotBandsFromInstallsOpHours(installs);
            }
        }
    }

    protected fetchDataForIndex(index, min: number, max?: number) {
        this.minDataRequested = Math.min(this.minDataRequested, min);
        if (max) this.maxDataRequested = Math.max(this.maxDataRequested, max, min);

        var ids;
        if (index === CHART_TYPE.HEATMAP || index === CHART_TYPE.PIE) {
            ids = this.graphedDashboardChannelIds;
        } else {
            ids = [Number(index)];
        }

        var d = $.Deferred<void>();
        if (this.workers[index]) {
            var getSeriesOptions: GetSeriesOptions = {
                workerId: this.workers[index],
                dashboardChannelIds: ids,
                chartConfig: this.chartConfig,
                startTimestamp: min,
                keepData: min >= this.minDataRequested,
                limitDataPoints: null,
            };
            if (max) getSeriesOptions['endTimestamp'] = max;
            // Handle daily custom range
            var dataRange = this.options.widget.getParamValue('CHART_DATA_RANGE', CHART_DATA_RANGE.DEFAULT);
            var fullRange = this.options.widget.getParamValue('DAILY_TIME_MODE', DAILY_TIME_MODE.DEFAULT);
            if (max && dataRange === CHART_DATA_RANGE.CUSTOM_DAILY && fullRange < 2 && max > moment().unix()) {
                // If we haven't completed today's range yet, switch to tomorrow
                var timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
                getSeriesOptions['endTimestamp'] = moment(Number(max) * 1000).tz(timezone).subtract(1, 'day').unix();
                getSeriesOptions['startTimestamp'] = moment(Number(min) * 1000).tz(timezone).subtract(1, 'day').unix();
            } else if (max) {
                getSeriesOptions['endTimestamp'] = max;
            }


            this.options.chartWorkerManager.getSeries(getSeriesOptions, (error, data) => {
                _.each(ids, (id: number) => {
                    this.channelsLoading = _.without(this.channelsLoading, id);
                });
                this.renderPlot(error, data);
                d.resolve();
            });
        }

        return d.promise();
    }

    protected fetchAllData(min: number, max?: number) {
        if (this.fetchAllDataPromise) {
            return this.fetchAllDataPromise;
        }

        var promises = _.map(_.keys(this.seriesMap), (dashboardChannelId) => {
            return this.fetchDataForIndex(dashboardChannelId, min, max);
        });

        return this.fetchAllDataPromise = $.when.apply($, promises).then(() => {
            this.fetchAllDataPromise = null;
        });
    }

    protected setChartButtonVisibility() {
        var type = this.getValueFromUIOrDefault('.chart-type', null, CHART_TYPE.DEFAULT);
        var isChartTypeDefault = this.options.widget.getParamValue('CHART_TYPE', CHART_TYPE.DEFAULT) === CHART_TYPE.DEFAULT;
        var isThresholdsDefault = this.options.widget.getParamValue('CHART_THRESHOLDS', CHART_THRESHOLDS.DEFAULT) === CHART_THRESHOLDS.DEFAULT;
        var isTimestampsDefault = this.options.widget.getParamValue('CHART_TIMESTAMPS', CHART_REAL_TIMESTAMPS.DEFAULT) === CHART_REAL_TIMESTAMPS.DEFAULT;
        var isLabelsDefault = this.options.widget.getParamValue('CHART_LABELS', CHART_LABELS.DEFAULT) === CHART_LABELS.DEFAULT;
        var isAggregationPeriodDefault = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT) === CHART_AGGREGATION_PERIOD.DEFAULT;

        if (_.includes([CHART_TYPE.LINE, CHART_TYPE.DEFAULT], type)) {
            if (isThresholdsDefault) {
                this.$('.chart-showthreshold').removeClass('hidden');
            }
            if (isTimestampsDefault) {
                this.$('.chart-timestamps').removeClass('hidden');
            }
        } else {
            this.$('.chart-timestamps').addClass('hidden');
            this.$('.chart-showthreshold').addClass('hidden');
        }

        if (isChartTypeDefault && isAggregationPeriodDefault) {
            this.$('.chart-period').removeClass('hidden');
        }

        if (isChartTypeDefault) {
            this.$('.clearfix').removeClass('hidden');
        }

        if (isLabelsDefault && _.includes([
            CHART_TYPE.STACKEDCOLUMN, CHART_TYPE.HEATMAP, CHART_TYPE.COLUMN, CHART_TYPE.PIE
        ], type)) {
            this.$('.chart-label').removeClass('hidden');
        } else {
            this.$('.chart-label').addClass('hidden');
        }

        this.$('.chart-button-hr').toggleClass('hidden', !isChartTypeDefault && !isThresholdsDefault && !isTimestampsDefault && !isLabelsDefault);
        this.$('.chart-period option[value=0]').prop('disabled', _.includes([
            CHART_TYPE.STACKEDCOLUMN, CHART_TYPE.HEATMAP, CHART_TYPE.COLUMN
        ], type));

        _.each(this.$('.w-chart select.dropdown').select2(<any>_.extend({}, Utils.Select2.defaultOptions, {
            minimumResultsForSearch: -1,
            width: 'style'
        })), (select) => {
            var dropdownContainer = $(select).data('select2').$container;
            dropdownContainer.addClass("dropdown");
        });

        this.$('.chart-type + .dropdown').toggleClass('hidden', !isChartTypeDefault);
        this.$('.chart-period + .dropdown').toggleClass('hidden', !isChartTypeDefault || !isAggregationPeriodDefault);
        this.$('.chart-stat + .dropdown').toggleClass('hidden', (!isChartTypeDefault || this.$('.chart-period').val() === '0'));

        this.$('.chart-data-range').toggleClass('hidden', !this.isDefaultChart());
        if (this.isDefaultChart()) {
            // Set up date selectors for custom data range
            var from = (<any>this.$('.chart-data-range .from'));
            var to = (<any>this.$('.chart-data-range .to'));

            from.datetimepicker({
                defaultDate: null
            });
            to.datetimepicker({
                defaultDate: null,
                useCurrent: false
            });
            from.on("dp.change", (e) => {
                to.data("DateTimePicker").minDate(e.date);
            });
            to.on("dp.change", (e) => {
                from.data("DateTimePicker").maxDate(e.date);
            });
        }
    }

    protected handleTimePickerChange(e: JQuery.UIEventBase) {
        var valid = this.isValidCustomDataRangeInput(this.getCustomDataRangeInput());

        if (valid || (!valid && this.previousTimePickerInputValid)) {
            this.previousTimePickerInputValid = valid;
            this.handleChangeChartType(e);
        }
    }

    protected handleTimeSelectorReset(e: JQuery.UIEventBase) {
        e.preventDefault();

        this.minDataRequested = Infinity;
        this.maxDataRequested = null;
        (<any>this.$('.chart-data-range .from')).val('');
        (<any>this.$('.chart-data-range .to')).val('');
        this.handleTimePickerChange(e);
    }

    protected handleClearAllSelections(e: JQuery.UIEventBase) {
        e.preventDefault();
        this.clearAllChannels();
        App.Bus.trigger('dashboard:clearAllSelections');
    }

    protected clearAllChannels() {
        this.reset();
    }

    protected handleChangeChartType(e: JQuery.UIEventBase) {
        var target = $(e.currentTarget);
        if (target.hasClass('chart-toggle-btn')) {
            target.siblings().removeClass('active');
            target.addClass('active');
        }

        var type = this.getValueFromUIOrDefault('.chart-type', null, CHART_TYPE.DEFAULT);
        var period = this.getValueFromUIOrDefault('.chart-period', null, CHART_AGGREGATION_PERIOD.DEFAULT);
        var showThresholdPlotlines = ChartHelper.isEnabled(this.getValueFromUIOrDefault('.chart-showthreshold span.active', 'data-value', CHART_THRESHOLDS.DEFAULT));
        var realTimestamps = ChartHelper.isEnabled(this.getValueFromUIOrDefault('.chart-timestamps span.active', 'data-value', CHART_REAL_TIMESTAMPS.DEFAULT));
        var convertAllValues = ChartHelper.isEnabled(this.getValueFromUIOrDefault('.chart-convert span.active', 'data-value', CHART_CONVERT_ALL_DATA.DEFAULT));
        var labels = ChartHelper.isEnabled(this.getValueFromUIOrDefault('.chart-label span.active', 'data-value', CHART_LABELS.DEFAULT));

        // This is not an option in the UI
        var hideSingleGaps = ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_HIDE_SINGLE_GAPS', CHART_HIDE_SINGLE_GAPS.DEFAULT));

        // Default to no aggregation period, but if one is needed, set to case 1 (hourly) and recalculate
        if ((type === CHART_TYPE.STACKEDCOLUMN || type === CHART_TYPE.HEATMAP || type === CHART_TYPE.COLUMN)
            && period === CHART_AGGREGATION_PERIOD.NONE) {
            this.$('.chart-period').val('1').trigger('change');
            return;
        }

        var ids = this.graphedDashboardChannelIds;

        // Update the chart configuration and generate the initial plot
        this.chartConfig = {
            seriesGroups: [],
            labels: labels,
            timezone: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser),
            showThresholdPlotlines: showThresholdPlotlines,
            realTimestamps: realTimestamps,
            convertAllValues: convertAllValues,
            hideSingleGaps: hideSingleGaps
        };
        this.generateSeriesGroupConfigs(ids);

        // Determine if we graphing a custom time frame, or the most recent data
        var dataRangeInput = this.getCustomDataRangeInput();
        if (this.isValidCustomDataRangeInput(dataRangeInput)) {
            var min = dataRangeInput.from.unix();
            var max = dataRangeInput.to.unix();
            var diff = max - min;
            this.resetAndLoadInitialPlot(min - diff, max + diff, ids).done(() => {
                this.setExtremes(min * 1000, max * 1000);
                this.setZoomButtonsForExtremesDifference((max - min) * 1000);
            });
        } else {
            this.resetAndLoadInitialPlot(this.minDataRequested, this.maxDataRequested, ids).done(() => {
                // Set zoom to hourly, so we don't load a ton of data just because the user reset the timepicker
                this.handleChartZoomButtonClick(0);
            });
        }
        this.setChartButtonVisibility();
    }

    protected isValidCustomDataRangeInput(input) {
        var customRange = input.fromText.length && input.toText.length;
        return customRange && input.from.isValid() && input.to.isValid() && input.to.isAfter(input.from);
    }

    protected getCustomDataRangeInput() {
        var timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
        var fromText = (<any>this.$('.chart-data-range .from')).val();
        var toText = (<any>this.$('.chart-data-range .to')).val();
        var from = moment.tz(fromText, this.dateTimePickerFormat, timezone);
        var to = moment.tz(toText, this.dateTimePickerFormat, timezone);

        return {
            from: from,
            to: to,
            fromText: fromText,
            toText: toText
        }
    }

    protected filterChannelIds(dashboardChannelIds) {
        // If this is not a default chart we must filter the channels first
        if (!this.isDefaultChart()) {
            dashboardChannelIds = _.filter(dashboardChannelIds, (id) => {
                if (!this.lastFilterValue) return true;

                var c = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
                return c.get('dashboard_channel_name').toLowerCase().indexOf(this.lastFilterValue.toLowerCase()) != -1;
            });
        }
        return dashboardChannelIds;
    }

    protected addInitialSeriesByChannelIds(ids: number[]) {
        this.initialChannelIds = ids;

        return _.map(this.filterChannelIds(ids), (id: number) => {
            return this.addChannel(id);
        });
    }

    protected handleFilterChange() {
        // Check if there is a pending visibility change
        var pendingGraphVisibilityChange = Boolean(this.filterChangeTimeout);
        clearTimeout(this.filterChangeTimeout);

        var isGraphHidden = this.$el.hasClass('hidden');
        var newVisibleChannels = this.filterChannelIds(this.initialChannelIds);

        // First hide or show the element bad on whether there is at least one channel to show
        if (!newVisibleChannels.length && this.lastFilterValue) {
            this.$el.addClass('hidden');
        } else {
            if (isGraphHidden) {
                this.$el.removeClass('hidden');
                pendingGraphVisibilityChange = true;
            }
        }

        // If the graph just became visible again we must reinitialize the entire graph
        if (pendingGraphVisibilityChange) {
            this.filterChangeTimeout = setTimeout(() => {
                this.resetAndLoadInitialPlot(this.minDataRequested, this.maxDataRequested, this.initialChannelIds);
            }, 1000);
        } else {
            // Add channels that are not yet graphed
            for (var i = 0; i < newVisibleChannels.length; i++) {
                if (!_.includes(this.graphedDashboardChannelIds, newVisibleChannels[i])) {
                    this.addChannel(newVisibleChannels[i]);
                }
            }

            // Removed channels that should no longer be graphed
            for (var i = 0; i < this.graphedDashboardChannelIds.length; i++) {
                if (!_.includes(newVisibleChannels, this.graphedDashboardChannelIds[i])) {
                    this.removeChannel(this.graphedDashboardChannelIds[i]);
                }
            }
        }
    }

    protected resetAndLoadInitialPlot(dataMin: number, dataMax: number, channelIds: number[]) {
        this.reset();
        this.minDataRequested = Math.min(dataMin, this.minDataRequested);
        this.maxDataRequested = Math.max(dataMax, this.maxDataRequested);

        // Add added series
        var deferreds = this.addInitialSeriesByChannelIds(channelIds);
        return $.when.apply($, deferreds)
    }

    render() {
        var timezones = new TimezoneCollection();
        var timezone = timezones.findWhere({ momentjs: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser) });

        this.$el.html(this.template({
            timezone: timezone,
            widget: this.options.widget,
            isDefaultChart: this.isDefaultChart(),
            dashboardId: this.options.dashboardId,
            permissionCollection: this.options.permissionCollection,
            editable: this.options.editable,
            movable: this.options.movable,
            cloneable: this.options.cloneable,
            deletable: this.options.deletable
        }));
        if (this.options.widget.getParamValue('CHART_IS_TALL', CHART_IS_TALL.DISABLED) === CHART_IS_TALL.ENABLED) {
            this.$el.addClass('tall');
        }
        this.$el.addClass('widget-w-' + this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.SIZE_6));
        this.$el.attr('data-widget-id', this.options.widget.get('id'));
        
        // Apply consistent height based on chart size (except for full size)
        const chartSize = this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.SIZE_6);
        const calculatedHeight = WIDGET_DIMENSIONS.getChartWidgetHeight(chartSize);
        if (calculatedHeight !== null) {
            // Apply height to the main body container and add flex layout class
            this.$('.body').css('height', calculatedHeight + 'px').addClass('w-chart-height-constrained');
        }

        this.$chartContainer = this.$('.w-highcharts');
        this.delegateEvents();

        if (!this.$chartContainer.is(':visible'))
            return;

        this.setChartButtonVisibility();
        this.renderPlot();

        // Get widget channels and sort them by greatest timestamp
        const widgetChannels = this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widget.get('id') });
        const widgetChannelSortedByTimestamp = _.sortBy(widgetChannels, channel => {
            const timestamp = channel.get('channel_last_timestamp');
            return timestamp !== undefined && timestamp !== null ? -timestamp : -Infinity;
        });
        const widgetChannelsIDs = _.map(widgetChannelSortedByTimestamp, channel => channel.get('dashboard_channel_id'));

        // Load initial data
        this.addInitialSeriesByChannelIds(widgetChannelsIDs);

        // Get selected channels
        this.requestSelectedChannels();

        // Try to fetch data every time the dashboard channel collection updates
        this.listenTo(this.options.dashboardChannelCollection, 'sync update', () => {
            this.updateChart();
        });

        return this;
    }

    private updateChart(){
        // Update the chart if we're not loading
        if (this.channelsLoading.length === 0) {
            var dataRange = ChartHelper.getChartDataRange(this.options.widget, this.options.currentUser, this.getCustomDataRangeInput.bind(this), this.isDefaultChart());

            var min, max;
            if (ChartHelper.isDataRangeDuration(dataRange.range)) {
                // if the chart is a duration, we will fetch the new data and add it at the end, unless the data is old
                // (i.e. the channel is no longer sending data)
                if ((this.maxDataRequested + 3600) < this.initTime) {
                    min = this.minDataRequested;
                    max = this.maxDataRequested;
                } else {
                    min = this.initTime;
                    max = this.getMaxLastChannelTimestamp();
                }
            } else {
                // if it is a fixed time frame we will just refetch that time frame in case anything has changed
                min = this.minDataRequested;
                max = this.maxDataRequested;
            }

            $.when(this.fetchAllData(min, max)).done(() => {
                if(this.isDefaultChart()){
                    // Update plotbands
                    const widgetChannels = [];
                    for (const id of this.graphedDashboardChannelIds) {
                        const channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
                        widgetChannels.push(channel);
                    }
                    //  Extract unique install_id values from channelsCollection
                    const installIds = [...new Set(
                        widgetChannels
                            .map(channel => channel.get('install_id'))
                            .filter(value => value !== undefined && value !== null && value !== '')
                            .filter(value => typeof value === 'string' || typeof value === 'number')
                    )];
                    // Fetch entries from installCollection that match the extracted install_id values
                    const installs = this.options.manager.options.installCollection.filter(
                        install => installIds.includes(install.get('id'))
                    );
                    this.addPlotBandsFromInstallsOpHours(installs);
                }
            });

        }
    }

    protected scheduleRedraw() {
        // When datagrouping is in effect, the chart oftentimes does not move the zoom window to the rightmost
        // position after the call to setData (if it was already in the right most position before setData)
        var zoomWindowIsRightMost = false;
        var windowSize = null;
        try {
            var extremes = this.chart.xAxis[0].getExtremes();

            // Check that the extremes exist and that the widow is to the right most position (within 5 seconds)
            if (extremes.dataMax && extremes.max && extremes.min && ChartHelper.withinConstant(extremes.dataMax, extremes.max, 5000)) {
                zoomWindowIsRightMost = true;
                windowSize = extremes.max - extremes.min;
            }
        } catch (e) {
        }

        if (this.channelsLoading.length === 0) {
            try {
                // The following line sometimes causes a bug in highcharts if chart.series is not defined
                // Or some series/point attributes are undefined
                this.chart.redraw(false);

                // Restore the zoom window to the right most position if necessary
                var extremes = this.chart.xAxis[0].getExtremes();

                // If there is only one data point, call setExtremes without arguments, which will center the point
                if (extremes.dataMin - extremes.dataMax === 0) {
                    this.setExtremes();
                } else if (zoomWindowIsRightMost) {
                    this.setExtremes(extremes.dataMax - windowSize, extremes.dataMax);
                } else if (this.options.widget.getParamValue('CHART_ZOOM_LEFT', '0') === '1') {
                    this.setExtremes(extremes.dataMin, extremes.dataMin + windowSize);
                }

                // Update plotbands
                const widgetChannels : DashboardChannel[] = [];
                for (const id of this.graphedDashboardChannelIds) {
                    const channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
                    widgetChannels.push(channel);
                }
                //  Extract unique install_id values from channelsCollection
                const installIds = [...new Set(
                    widgetChannels
                        .map(channel => channel.get('install_id'))
                        .filter(value => value !== undefined && value !== null && value !== '')
                        .filter(value => typeof value === 'string' || typeof value === 'number')
                )];
                if(installIds.length != 0) {
                    // Fetch entries from installCollection that match the extracted install_id values
                    const installCollection = this.options.manager?.options?.installCollection;
                    if(installCollection){
                        const installs = installCollection.filter(
                            install => installIds.includes(install.get('id'))
                        );
                        this.addPlotBandsFromInstallsOpHours(installs);
                    }
                }

                this.hideLoading();
            } catch (e) {
                //This can also occur when the series is "defined" but not complete
                console.log('Highcharts Bug: chart.series was not defined');
            }
        }
    }

    protected renderPlot(error?, data?: any) {
        if (!this.chart) {
            this.initializeChart();
            return;
        }

        if (!data || !this.graphedDashboardChannelIds.length) return;


        _.each(data, (series: any, seriesTag) => {
            // Check if it's a heatmap
            if (seriesTag === CHART_TYPE.HEATMAP) {
                var seriesGroup = _.find(this.chartConfig.seriesGroups, { type: CHART_TYPE.HEATMAP });
                var names = _.map(seriesGroup.series, 'name');

                var filtered: number[] = _.filter(_.map(series, (a) => {
                    return a[2]
                }), (a) => {
                    return a != null
                });
                if (filtered.length === 0) filtered.push(0);
                var min = Math.min.apply(this, filtered);
                var max = Math.max.apply(this, filtered);

                this.chart.yAxis[0].setCategories(names, false);
                this.seriesMap[seriesTag].setData(series, false);

                // We need to set the "column size" of our map, since our xAxis is in milli seconds
                // Otherwise Highcharts will assume our xAxis is consecutive and will insert a column for every ms
                if (series.length > 1 && names.length) {
                    var initial = series[0][0];

                    for (var i = 1; i < series.length; i++) {
                        if (series[i][0] <= initial) continue;

                        var columnSize = series[i][0] - initial;
                        (<any>this.seriesMap[seriesTag]).update({ colsize: columnSize });
                        break;
                    }
                }

                setTimeout(() => {
                    // Update the color mapping of the color axis
                    this.chart.colorAxis[0].setExtremes(min, max, false);
                    this.chart.colorAxis[0].update({ tickInterval: (max - min) / 40 });
                }, 0);
            } else if (this.chart.userOptions.chart.type === "gauge") {
                var value = _.last(_.last(series));
                $(".timestamp." + this.options.widget.id).text(Utils.Time.getChannelLastSeenString(this.options.dashboardChannelCollection.get(seriesTag).get('channel_last_timestamp')));
                value = Utils._getFormattedValue(value, this.options.dashboardChannelCollection.get(seriesTag).get('sensor_type'), this.options.dashboardChannelCollection.get(seriesTag).get('sensor_version'), Utils._getFormatDecimals(this.options.dashboardChannelCollection.get(seriesTag).get('channel_decimals'), value), this.options.dashboardChannelCollection.get(seriesTag).get('channel_conversion_function_name'));
                var colorData = this.getState(this.options.dashboardChannelCollection.get(seriesTag));
                this.seriesMap[seriesTag].setData([Number(value)], false);
                this.seriesMap[seriesTag].update(<any>{ dataLabels: { color: colorData } }, false);
            } else {
                if (_.has(this.seriesMap, seriesTag)) this.seriesMap[seriesTag].setData(series, false);
            }
        });
        this.scheduleRedraw();
    }

    protected getState(channel: DashboardChannel) {
        let color = Utils.colors.green;
        const range = channel.getRange();
        if (range) color = range.get('color');

        return color;
    }

    protected showLoading() {
        // Catch an error that occurs when highcharts is not yet fully initialized
        try {
            this.chart.hideNoData();
            this.chart.showLoading();
        } catch (e) {
        }
    }

    protected hideLoading() {
        this.chart.hideLoading();
        setTimeout(() => {
            if (!this.chart) return;

            if (!this.chart.series || !this.chart.series.length) {
                // Catch an error that occurs when highcharts is not yet fully initialized
                try {
                    this.chart.showNoData();
                } catch (e) {
                }
            } else {
                var dataFound = false;
                for (var i = 0; i < this.chart.series.length; i++) {
                    if (this.chart.series[i].xData.length) dataFound = true;
                }

                if (!dataFound) this.chart.showNoData();
            }
        }, 1000);
    }

    protected updateExtremes(newTsDiff: number) {
        // Make sure a chart is fully initialized
        if (!this.chart || !this.chart.xAxis || !this.chart.xAxis.length) return;

        var extremes = this.chart.xAxis[0].getExtremes();

        if (extremes.dataMin == null || extremes.dataMax == null || !newTsDiff) {
            // If we haven't loaded any data yet, set up pending extremes and return
            return this.pendingGraphExtremes = newTsDiff;
        }

        // If there is only a single datapoint, simply call setExtremes without arguments
        // this will center the point in the graph
        if ((extremes.dataMax - extremes.dataMin) === 0) {
            this.setExtremes();
            return this.pendingGraphExtremes = null;

        } else if ((extremes.dataMax - extremes.dataMin) < newTsDiff) {
            // If our zoom is bigger than the available data
            this.setExtremes(extremes.dataMin, extremes.dataMax);

            // Set up pending extremes so that once we loaded more data we will update the extremes again
            return this.pendingGraphExtremes = newTsDiff;
        }

        // If there were different pending graph extremes from a previous call, reset them
        this.pendingGraphExtremes = null;
        var newMin, newMax;
        if ((extremes.max - extremes.min) == newTsDiff) {
            // If the zoom is already correct do nothing
            newMin = extremes.min;
            newMax = extremes.max;
        } else if (extremes.max === extremes.dataMax) {
            // If the zoom window is all the way to the right
            newMin = extremes.max - newTsDiff;
            newMax = extremes.max;
        } else if (extremes.min === extremes.dataMin) {
            // If the zoom window is all the way to the left
            newMin = extremes.min;
            newMax = extremes.min + newTsDiff;
        } else if ((extremes.max - extremes.min) > newTsDiff) {
            // if we're zooming IN somewhere in the middle
            var midpoint = extremes.min + ((extremes.max - extremes.min) / 2);
            newMin = midpoint - Math.floor(newTsDiff / 2);
            newMax = midpoint + Math.ceil(newTsDiff / 2);
        } else {
            // if we're zooming OUT somewhere in the middle
            extremes.max = Math.floor(extremes.max);

            var rightDistance = Math.min(extremes.dataMax - extremes.max, Math.floor(newTsDiff / 2));
            var leftDistance = newTsDiff - rightDistance;
            newMin = extremes.max - leftDistance;
            newMax = extremes.max + rightDistance;
        }
        this.setExtremes(newMin, newMax);

        // Since the extremes might have changes we must refresh the buttons;
        // This will usually also be called first by afterSetExtremes, but if the extremes are the same
        // it may not be called, so we're calling it here
        this.setZoomButtonsForExtremesDifference(newMax - newMin);
    }

    protected setExtremes(min?: number, max?: number) {
        // This is a thin wrapper around highchart's setExtremes function.
        if (!this.chart || !this.chart.xAxis) return;

        if (min && max) {
            this.chart.xAxis[0].setExtremes(min, max);
        } else {
            // If there is only a single datapoint, simply call setExtremes without arguments
            // this will center the point in the graph
            this.chart.xAxis[0].setExtremes();
        }

        // The following will force re-initialize the navigator. This is only necessary if the series that
        // was added to the chart first is removed, since the navigator somehow loses track of the extremes
        // of its base series when this happens
        if (this.chart.navigator && this.chart.navigator.xAxis) {
            this.chart.navigator.update();
            this.chart.navigator.xAxis.setExtremes();
        }
    }

    protected getMaxLastChannelTimestamp() {
        var widgetChannels = this.options.dashboardChannelCollection.filter((m) => {
            return _.includes(this.graphedDashboardChannelIds, m.get('dashboard_channel_id'))
        });
        var maxLastTimestampChannel = _.maxBy(widgetChannels, (m) => {
            return m.get('channel_last_timestamp')
        });
        return maxLastTimestampChannel ? maxLastTimestampChannel.get('channel_last_timestamp') : Math.floor(new Date().getTime() / 1000);
    }

    protected handleChartZoomButtonClick(zoomLevel) {
        var button = _.find(ChartHelper.CHARTZOOMBUTTONS, { zoomLevel: zoomLevel });

        // Check if an invalid button number was passed in order to deactivate all buttons
        if (!button) return;

        // Make sure there is something to load. If not just set the extreme as pending
        if (_.size(this.seriesMap) === 0) {
            this.pendingGraphExtremes = button.extremesDifference;
            return;
        }

        var widgetChannels = this.options.dashboardChannelCollection.filter((m) => {
            return _.includes(this.graphedDashboardChannelIds, m.get('dashboard_channel_id'))
        });

        this.activateZoomButton(zoomLevel);

        var max = this.getMaxLastChannelTimestamp();
        var since;
        switch (zoomLevel) {
            case CHART_DATA_ZOOM.HOUR_1:
                since = moment(max * 1000).subtract(1, 'hour').unix();
                break;
            case CHART_DATA_ZOOM.DAY_1:
                since = moment(max * 1000).subtract(1, 'day').unix();
                break;
            case CHART_DATA_ZOOM.WEEK_1:
                since = moment(max * 1000).subtract(1, 'week').unix();
                break;
            case CHART_DATA_ZOOM.MONTH_1:
                since = moment(max * 1000).subtract(1, 'month').unix();
                break;
            case CHART_DATA_ZOOM.MONTH_3:
                since = moment(max * 1000).subtract(3, 'month').unix();
                break;
            case CHART_DATA_ZOOM.MONTH_6:
                since = moment(max * 1000).subtract(6, 'month').unix();
                break;
            case CHART_DATA_ZOOM.YEAR_1:
                since = moment(max * 1000).subtract(1, 'year').unix();
                break;
            case 'Infinity':
                var minCreatedTimestampChannel = _.minBy(widgetChannels, (m) => {
                    return m.get('channel_created')
                });
                since = minCreatedTimestampChannel.get('channel_created');
                break;
        }

        var dataRange = ChartHelper.getChartDataRange(this.options.widget, this.options.currentUser, this.getCustomDataRangeInput.bind(this), this.isDefaultChart());
        if (this.minDataRequested <= since || !ChartHelper.isDataRangeDuration(dataRange.range)) {
            this.updateExtremes(button.extremesDifference);
        } else {
            // Mark all channels as loading to prevent getting interrupted by other updates while we're loading
            this.channelsLoading = _.uniq(this.channelsLoading.concat(this.graphedDashboardChannelIds));

            this.showLoading();

            $.when(this.fetchAllData(since, max)).done(() => {
                this.updateExtremes(button.extremesDifference);

                //  Extract unique install_id values from channelsCollection
                const installIds = [...new Set(
                    widgetChannels
                        .map(channel => channel.get('install_id'))
                        .filter(value => value !== undefined && value !== null && value !== '')
                        .filter(value => typeof value === 'string' || typeof value === 'number')
                )];
                // Fetch entries from installCollection that match the extracted install_id values
                const installs = this.options.manager.options.installCollection.filter(
                    install => installIds.includes(install.get('id'))
                );
                // recalculate Plotbands
                if(this.isDefaultChart()){
                    this.addPlotBandsFromInstallsOpHours(installs);
                }
            });
        }
    }

    protected activateZoomButton(zoomLevel?: string) {
        var i = _.findIndex(ChartHelper.CHARTZOOMBUTTONS, { zoomLevel: zoomLevel });
        _.each(this.chartButtons, (button, j) => {
            button.setState(i === j ? 2 : 0);
        });
        this.lastZoomLevel = zoomLevel;
    }

    protected setZoomButtonsForExtremesDifference(diff) {
        // For some reason, when moving the zoom window the actual tsdiffence gets bigger.
        // so instead of testing equality, we check that it's within 2% of the expected
        var buttonFound = false;
        _.each(ChartHelper.CHARTZOOMBUTTONS, (button) => {
            if (ChartHelper.withinPercent(diff, button.extremesDifference, 0.02)) {
                this.activateZoomButton(button.zoomLevel);
                buttonFound = true;
                return false;
            }
        });

        // Special case for 'all'
        if (this.pendingGraphExtremes === Infinity) {
            return this.activateZoomButton('Infinity');
        }

        // If extremes were manually set by the user, we won't have found them. Deactivate all buttons
        if (!buttonFound) this.activateZoomButton();
    }

    protected initializeChart() {
        this.chartButtons = [];

        var afterSetExtremes = () => {
            var newExtremes = this.chart.xAxis[0].getExtremes();
            this.setZoomButtonsForExtremesDifference(newExtremes.max - newExtremes.min);
        };

        var afterRedraw = () => {
            // Are we done fetching data or did the user adjust the bar
            if (this.channelsLoading.length === 0) {
                // Do we yet have to apply the new zoom level?
                if (this.pendingGraphExtremes) {
                    // Delete the pending change,
                    //
                    // Calling setExtremes right after redraw frequently causes a Highcharts error
                    setTimeout(() => {
                        this.updateExtremes(this.pendingGraphExtremes);
                        this.pendingGraphExtremes = null;
                    }, 100);
                }
                this.activateZoomButton(this.lastZoomLevel);
            }
        };

        this.chart = (<any>Highcharts).stockChart(this.$chartContainer.get(0), this.getHighchartsConfig.call(
            this, { redraw: afterRedraw }, { afterSetExtremes: afterSetExtremes })
        );

        // If this is a default chart, use the user timezone.
        let timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
        if(this.isDefaultChart()) timezone = this.options.currentUser.get('timezone');

        // Somehow if this update is not called highcharts does not use the correct timezone (even though it is part
        // of the initial configuration)... Maybe a bug in Highcharts?
        this.chart.update({
            time: {
                timezone: timezone
            }
        });

        // Only show the buttons if this is the default chart
        if (this.isDefaultChart()) {
            var range = ChartHelper.getChartDataRange(this.options.widget, this.options.currentUser, this.getCustomDataRangeInput.bind(this), this.isDefaultChart());
            var rangeIsCustom = range.range === CHART_DATA_RANGE.CUSTOM;
            var rangeDifference = (range.to - range.from) * 1000;

            // Configure the Load Data/Zoom buttons
            var showLabel = this.$chartContainer.width() > 700;
            var marginLeft = 3;
            var buttonGap = 7;
            var buttonWidth = 30;
            var buttonTop = 10;
            var textTop = 30;

            var buttonNormalState = {
                fill: '#f7f7f7',
                "stroke-width": 0,
                width: 17,
                r: 0,
                paddingLeft: 5,
                height: 12,
                style: {
                    cursor: 'pointer',
                    'font-weight': 'normal',
                    'z-index': '10'
                }
            };
            var buttonHoverState = {
                'fill': '#e7e7e7',
                style: {
                    'font-weight': 'bold',
                    'z-index': '10'
                }
            };
            var buttonPressedState = {
                fill: '#e7e7e7',
                style: {
                    'font-weight': 'bold',
                    'z-index': '10'
                }
            };

            var labelWidth;
            if (showLabel) {
                var label;
                if (rangeIsCustom) {
                    label = 'Zoom:';
                    labelWidth = 40;
                } else {
                    label = 'Load Data and Zoom:';
                    labelWidth = 140;
                }
                this.chart.renderer.text(label, marginLeft, textTop).add();
            } else {
                labelWidth = 0;
            }

            // Add all the zoom buttons and assign them a zoom number
            _.each(ChartHelper.CHARTZOOMBUTTONS, (b, i) => {
                // If the range is custom, hide buttons that are not applicable for the selected range
                if (rangeIsCustom && rangeDifference < b.extremesDifference) return;

                var x = marginLeft + labelWidth + (i + 1) * buttonGap + i * buttonWidth;
                this.chartButtons.push(
                    this.chart.renderer.button(b.label, x, buttonTop, () => {
                        this.handleChartZoomButtonClick(b.zoomLevel);
                    }, buttonNormalState, buttonHoverState, buttonPressedState).add()
                );
            });
        }
    }

    protected getGaugeThresholds(min, max) {
        let innerR = '90%';
        let outerR = '105%';

        // Set the innerRadius depending on the gauge size
        const size = this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.DEFAULT);
        switch (size) {
            case CHART_SIZE.SIZE_6:
            case CHART_SIZE.SIZE_4:
            case CHART_SIZE.SIZE_3:
                innerR = '95%';
                break;
            case CHART_SIZE.SIZE_2:
                innerR = '93%';
                break;
        }

        let position = 0;
        _.each(this.options.dashboardChannelCollection.models, dshChannel => {
            if (dshChannel.get("dashboard_channel_id") === this.initialChannelIds[0])
                return false;
            position++;
        });

        const channel = this.options.dashboardChannelCollection.models[position];
        const rangesList = channel.get('rangesList');
        const gaugeRanges = [{
            from: min,
            to: max,
            color: '#e7f2f8',
            innerRadius: innerR,
            outerRadius: '110%'
        }];
        for (const range of rangesList) {
            // do not exceed min and max values set by gauge
            let from = min;
            let to = max;
            if (range.min && range.min > min) from = range.min;
            if (range.max && range.max < max) to = range.max;

            const gaugeRange = {
                from: from,
                to: to,
                innerRadius: innerR,
                outerRadius: outerR,
                color: range.color
            }
            gaugeRanges.push(gaugeRange);
        }

        return gaugeRanges;
    }

    protected getYAxisConfig(chartConfigGroup, chartConfigSeries) {
        var unit = chartConfigSeries.valueSuffix;
        var view = this;
        var title = {
            align: "high",
            rotation: 0,
            text: this.chartConfig.convertAllValues ? unit : '',
            margin: -14,
            x: -Math.max((unit.length - 2), 0) * 5,
            y: -12,
            style: {}
        };
        if (chartConfigGroup.type === CHART_TYPE.GAUGE) {
            var min = +this.options.widget.getParamValue('GAUGE_LOWER_LIMIT', CHART_THRESHOLDS.DEFAULT);
            var max = +this.options.widget.getParamValue('GAUGE_UPPER_LIMIT', CHART_THRESHOLDS.DEFAULT);
            var thresholds = this.getGaugeThresholds(min, max);
        }

        switch (chartConfigGroup.type) {
            case CHART_TYPE.DEFAULT:
            case CHART_TYPE.LINE:
            case CHART_TYPE.STACKEDAREA:
                return {
                    title: title,
                    plotBands: [],
                    gridLineWidth: 1,
                    labels: { enabled: true },
                    opposite: true
                };
            case CHART_TYPE.COLUMN:
                return {
                    softMin: 0,
                    title: title,
                    opposite: true,
                };
            case CHART_TYPE.BOX_PLOT:
                return {
                    title: title,
                    opposite: true,
                };
            case CHART_TYPE.STACKEDCOLUMN:
                //We have a formatter for stacked column here
                //Because this applies to the totals, not the individual data
                return {
                    softMin: 0,
                    title: title,
                    opposite: true,
                    stackLabels: {
                        enabled: view.chartConfig.labels,
                        color: '#000000',
                        formatter: function () {
                            if (_.size(this.axis.series) > 0) {
                                var decimals = this.axis.series[0].tooltipOptions.valueDecimals;
                                var units = this.axis.series[0].tooltipOptions.valueSuffix;
                                return this.total != null ? (this.total).toFixed(decimals) + " " + units : '';
                            }
                            return;
                        },
                    }
                };
            case CHART_TYPE.PIE:
                return {
                    title: title
                }
            case CHART_TYPE.GAUGE:
                return {
                    min: min,
                    max: max,

                    lineColor: null,
                    minorTickInterval: 'auto',
                    minorTickWidth: 1,
                    minorTickLength: 10,
                    minorTickPosition: 'inside',
                    minorTickColor: '#060606',

                    tickPixelInterval: 30,
                    tickWidth: 2,
                    tickPosition: 'inside',
                    tickLength: 17,
                    tickColor: '#060606',
                    labels: {
                        rotation: 'auto',
                        step: 2,
                        distance: -33,
                        useHTML: true,
                        y: 0,
                        style: {
                            fontFamily: 'Poppins',
                            fontWeight: '400',
                            fontSize: '12px',
                            textShadow: '0',
                            color: '#aaaaaa'
                        }
                    },
                    plotBands: thresholds
                }
        }
    }

    protected getHighchartsConfig(chartEvents, xAxisEvents) {
        var view = this;
        var config = this.getBaseConfig(chartEvents, xAxisEvents);

        // Line
        config.xAxis['ordinal'] = false;

        // Bar
        config.yAxis = {
            softMin: 0,
            title: null
        };

        // Stacked bar
        var stackedColumnTooltip = function (points) {
            // Group points by stack
            var str = '<hr>';
            var totalStrings = '';
            var stackPoints = _.groupBy(points, function (p) {
                return (<any>p).series.stackKey;
            });
            _.each(stackPoints, function (points) {
                var decimals = (<any>points[0]).series.tooltipOptions.valueDecimals;
                var units = (<any>points[0]).series.tooltipOptions.valueSuffix;

                // Show individual values
                for (var i = 0; i < points.length; i++) {
                    var val = (<any>points[i]).y == null ? '' : ((<any>points[i]).y).toFixed(decimals) + units;

                    str += '<tspan style="color:' + (<any>points[i]).color + ';">' +
                        (<any>points[i]).series.name + ': </tspan><b>' + val + '</b><br/>'
                }
                if (points.length > 1) {
                    // Get the stack total
                    var total = (<any>points[0]).total != null ? ((<any>points[0]).total).toFixed(decimals) + units : '';
                    var totalStr = '<b>' + total + '</b>';
                    totalStrings += 'Stacked Total' + (units ? ' ' + units : '') + ': ' + totalStr + '<br>';
                }

            });

            return [str + totalStrings];
        };

        // Stacked area
        var stackedAreaTooltip = function (points) {
            // Group points by stack
            var str = '<hr>';
            var totalStrings = '';
            var stackPoints = _.groupBy(points, function (p) {
                return (<any>p).series.stackKey;
            });
            _.each(stackPoints, function (points) {
                var decimals = (<any>points[0]).series.tooltipOptions.valueDecimals;
                var units = (<any>points[0]).series.tooltipOptions.valueSuffix;

                // Show individual values
                for (var i = 0; i < points.length; i++) {
                    var val = (<any>points[i]).y == null ? '' : ((<any>points[i]).y).toFixed(decimals) + units;

                    str += '<tspan style="color:' + (<any>points[i]).color + ';">' +
                        (<any>points[i]).series.name + ': </tspan><b>' + val + '</b><br/>'
                }
                if (points.length > 1) {
                    // Get the stack total
                    var total = (<any>points[0]).total != null ? ((<any>points[0]).total).toFixed(decimals) + units : '';
                    var totalStr = '<b>' + total + '</b>';
                    totalStrings += 'Stacked Total' + (units ? ' ' + units : '') + ': ' + totalStr + '<br>';
                }
            });

            return [str + totalStrings];
        };

        // Box Plot
        var boxPlotTooltip = function (points) {
            var entries = [];
            _.each(points, (point: any) => {
                var decimals = point.series.userOptions.tooltip.valueDecimals;
                var units = point.series.userOptions.tooltip.valueSuffix;
                units = units ? units : '';

                console.log(point);
                var str = '<tspan style="color:' + point.color + ';">' + point.series.name + ': </tspan>';
                _.each({
                    'min': point.point.low,
                    'q1': point.point.q1,
                    'med': point.point.median,
                    'q3': point.point.q3,
                    'max': point.point.high,
                }, (v, k) => {
                    str += '<b>' + (v.toFixed(decimals)) + units + '</b> (' + k + '); ';
                });
                entries.push(str)
            });
            return entries
        };

        // Pie
        var pieTooltip = function (points) {
            var entries = [];
            _.each(points, (point: any) => {
                var decimals = point.series.userOptions.tooltip.valueDecimals;
                var units = point.series.userOptions.tooltip.valueSuffix;

                var val = point.y == null ? '' : point.y.toFixed(decimals) + units;

                var str = '<tspan style="color:' + point.color + ';">' + point.category + '</tspan><br>';
                if (point.name) {
                    str += point.name + ': ';
                }
                str += '<b>' + val + '</b>';
                entries.push(str)
            });
            return entries
        };

        var defaultTooltip = function (points) {
            var entries = [];
            _.each(points, (point: any, i) => {
                var decimals = point.series.userOptions.tooltip.valueDecimals;
                var units = point.series.userOptions.tooltip.valueSuffix;

                var str = '';

                if (Number(i) === 0) str += "<hr>";
                str += '<span style="color:' + point.color + '">'
                    + point.series.name + ': </span><b>' + point.y.toFixed(decimals)
                    + ' ' + units + '</b>';
                entries.push(str);
            });
            return entries;
        };

        var heatmapTooltip = function (points) {
            var entries = [];
            _.each(points, (point: any, i) => {
                var decimals = point.series.userOptions.tooltip.valueDecimals;
                var units = point.series.userOptions.tooltip.valueSuffix;
                var val = point.value != null ? (point.value).toFixed(decimals) + " " + units : '';
                var str = '';
                if (Number(i) === 0) str += "<hr>";
                str += point.series.yAxis.categories[point.y] + ': <b>' +
                    val + '</b>';
                entries.push(str);
            });
            return entries;
        };

        config.chart['plotBorderWidth'] = 0;
        config['yAxis'] = {
            title: null,
            labels: { enabled: false }
        };
        config['legend'] = {
            align: 'right',
            layout: 'vertical',
            margin: 0,
            verticalAlign: 'top',
            y: 25,
            symbolHeight: 280
        };

        var types = _.uniq(_.map(this.chartConfig.seriesGroups, 'type'));

        var isHeatmap = _.some(types, (type) => {
            return type === CHART_TYPE.HEATMAP
        });
        if (isHeatmap) {
            config['colorAxis'] = {
                min: null,
                max: null,
                minColor: '#FFFFFF',
                maxColor: Utils.colors.red,
                labels: {
                    enabled: true
                },
                showInLegend: _.some(types, (type) => {
                    return type === CHART_TYPE.HEATMAP
                }),
            };
        }

        var isGauge = _.some(types, (type) => {
            return type === CHART_TYPE.GAUGE
        });

        if (isGauge) {
            config['pane'] = {
                startAngle: -120,
                endAngle: 120,
                background: null,
                size: '93%'
            }
            switch (this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.SIZE_6)) {
                case CHART_SIZE.SIZE_1: // tiny
                    config.chart.marginTop = 0;
                    config.chart.marginBottom = -80;
                    break;
                case CHART_SIZE.SIZE_2: // small
                    config.chart.marginTop = 40;
                    config.chart.marginBottom = -80;
                    break;
                case CHART_SIZE.SIZE_3: // medium
                    config.chart.marginTop = 50;
                    config.chart.marginBottom = -80;
                    break;
                case CHART_SIZE.SIZE_4: // big
                case CHART_SIZE.SIZE_6: // full
                    config.chart.marginTop = 60;
                    config.chart.marginBottom = -80;
                    break;
                default:
                    config.chart.marginTop = 40;
                    config.chart.marginBottom = -80;
                    break;
            }
            config.chart.type = "gauge";
        }


        config['plotOptions'] = {
            // line specific config
            line: {
                threshold: null,
                zIndex: 3,
            },

            // bar specific config
            column: {
                dataLabels: {
                    enabled: this.chartConfig.labels,
                    formatter: function () {
                        var decimals = this.series.tooltipOptions.valueDecimals;
                        var units = this.series.tooltipOptions.valueSuffix;
                        return String(this.y.toFixed(decimals)) + units;
                    }
                },
                animation: false,
                pointPadding: 0.2,
                borderWidth: 0,
            },
            // Stacked area specific config
            area: {
                stacking: 'normal',
                lineColor: '#666666',
                lineWidth: 1,
                marker: {
                    lineWidth: 1,
                    lineColor: '#666666'
                }
            },

            // Pie config
            chart: {
                plotBackgroundColor: null,
                plotBorderWidth: 0,
                plotShadow: false,
            },
            pie: {
                center: ['50%', '50%'],
                innerSize: "45%",
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: view.chartConfig.labels,
                    formatter: function () {
                        var str = '';
                        var decimals = this.point.series.tooltipOptions.valueDecimals;
                        var needCategory = _.some(this.series.points, (dataPoint) => {
                            return this.point['category'] !== dataPoint['category'];
                        });
                        var needName = _.some(this.series.points, (dataPoint) => {
                            return this.point['name'] !== dataPoint['name'];
                        });
                        if (needCategory) str += this.point['category'] + ' ';
                        if (needName) str += this.point['name'] + ' ';

                        str += String(this.point.percentage.toFixed(decimals)) + '%';
                        return str;

                    },
                    distance: this.$chartContainer.width() * .02
                },
                xAxis: null,
                tooltip: null,
                exporting: null
            },
            //heatmap specific config
            heatmap: {
                dataLabels: {
                    enabled: view.chartConfig.labels,
                    color: '#000000',
                    formatter: function () {
                        var decimals = view.chart.series[0].tooltipOptions.valueDecimals;
                        var units = view.chart.series[0].tooltipOptions.valueSuffix;
                        if (typeof (this.point.value) === 'number') {
                            return this.point.value != null ? (this.point.value).toFixed(decimals) + " " + units : '';
                        }
                    }
                },
            },
            gauge: {
                dataLabels: {
                    borderColor: 'transparent',
                    useHTML: true,
                    formatter: function () {
                        var units = view.chart.series[0].tooltipOptions.valueSuffix;
                        return '<span class="rpGaugeLabel" style="text-align:center;display:block;">' + this.y + '<span style="font-size:12px;text-align:center;display:block;">' + units + '</span></span>'
                    },
                    style: {
                        fontFamily: 'Poppins',
                        fontWeight: '300',
                        fontSize: '18px',
                        textShadow: '0',
                        color: '#666'
                    },
                },
                dial: {
                    radius: '75%',
                    backgroundColor: '#ccc',
                    borderWidth: 0,
                    baseWidth: 5,
                    topWidth: 1,
                    baseLength: '40%', // of radius
                    rearLength: '0%'
                },
                pivot: {
                    backgroundColor: '#bbb',
                    radius: 7
                },
                tooltip: null
            }
        };
        // have dafault chart gaps not connect even if zoomed out
        // TODO seperate system status graph view to its own view
        if (this.isDefaultChart() && !ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_HIDE_SINGLE_GAPS', CHART_HIDE_SINGLE_GAPS.DEFAULT))) {
            var pollingFrequency = this.getPollingFrequencyForSystemStatus();
            if (pollingFrequency) {
                config.plotOptions.line = _.extend(config.plotOptions.line, {
                    gapSize: 3 * 1000 * pollingFrequency,
                    gapUnit: 'value'
                })
            }
        }

        config['tooltip'] = {
            formatter: function () {
                var allPoints = [];
                var tooltipPieces = [];

                if (this.points) allPoints = _.concat(allPoints, this.points);
                if (this.point) allPoints.push(this.point);

                var offset = moment.duration(view.chart.time.getTimezoneOffset(), 'milliseconds');
                var hours = offset.get('hours');
                var minutes = offset.get('minutes');
                var sign = (_.includes([-0, -1], Math.sign(hours)) ? '+' : '-');
                var timezoneAddon = ' (UTC' + sign + Utils.zeroPad(Math.abs(hours), 2) + ':' + Utils.zeroPad(Math.abs(minutes), 2) + ')';
                var dateHead = '<b>' + view.chart.time.dateFormat('%A, %b %e, %H:%M:%S' + timezoneAddon, allPoints[0].x) + '</b>';
                var showDateHead = false;

                _.each(_.groupBy(allPoints, (point) => {
                    return point.series.userOptions.rawType;
                }), (pointsByType, type) => {
                    if (type === CHART_TYPE.STACKEDCOLUMN) {
                        showDateHead = true;
                        tooltipPieces = _.concat(tooltipPieces, stackedColumnTooltip(pointsByType));
                    } else if (type === CHART_TYPE.STACKEDAREA) {
                        showDateHead = true;
                        tooltipPieces = _.concat(tooltipPieces, stackedAreaTooltip(pointsByType));
                    } else if (type === CHART_TYPE.PIE) {
                        tooltipPieces = _.concat(tooltipPieces, pieTooltip(pointsByType));
                    } else if (type === CHART_TYPE.HEATMAP) {
                        showDateHead = true;
                        tooltipPieces = _.concat(tooltipPieces, heatmapTooltip(pointsByType));
                    } else if (type === CHART_TYPE.BOX_PLOT) {
                        showDateHead = true;
                        tooltipPieces = _.concat(tooltipPieces, boxPlotTooltip(pointsByType));
                    } else {
                        showDateHead = true;
                        tooltipPieces = _.concat(tooltipPieces, defaultTooltip(pointsByType));
                    }
                });
                if (showDateHead) tooltipPieces.unshift(dateHead);
                return tooltipPieces.join('<br>');
            },
            useHTML: true,
            split: false,
            shared: true,
            backgroundColor: '#f8f8f8',
            outside: true, // Ensure tooltips display outside the chart
        };
        if (isGauge)
            config['tooltip']['enabled'] = false;
        return config;

    }

    protected isDefaultChart() {
        return this.options.widget.get('dashboard_widget_type_id') === 4
    }

    protected hasNavigator() {
        var chartType = this.options.widget.getParamValue('CHART_TYPE', CHART_TYPE.LINE);
        if (chartType === CHART_TYPE.PIE || chartType === CHART_TYPE.GAUGE) return false;

        var chartZoom = this.options.widget.getParamValue('CHART_DATA_ZOOM', CHART_DATA_ZOOM.DEFAULT);
        var chartRange = ChartHelper.getChartDataRange(this.options.widget, this.options.currentUser, this.getCustomDataRangeInput.bind(this), this.isDefaultChart());

        if (chartRange.range === CHART_DATA_RANGE.CUSTOM || chartRange.range === CHART_DATA_RANGE.CUSTOM_MONTHLY_BILLING) {
            var from = chartRange.from;
            var to = chartRange.to;

            switch (chartZoom) {
                case CHART_DATA_ZOOM.HOUR_1:
                    return moment(to * 1000).diff(from * 1000, 'hour', true) > 1;
                case CHART_DATA_ZOOM.DAY_1:
                    return moment(to * 1000).diff(from * 1000, 'day', true) > 1;
                case CHART_DATA_ZOOM.WEEK_1:
                    return moment(to * 1000).diff(from * 1000, 'week', true) > 1;
                case CHART_DATA_ZOOM.MONTH_1:
                    return moment(to * 1000).diff(from * 1000, 'month', true) > 1;
                case CHART_DATA_ZOOM.MONTH_3:
                    return moment(to * 1000).diff(from * 1000, 'month', true) > 3;
                case CHART_DATA_ZOOM.MONTH_6:
                    return moment(to * 1000).diff(from * 1000, 'month', true) > 6;
                case CHART_DATA_ZOOM.YEAR_1:
                    return moment(to * 1000).diff(from * 1000, 'year', true) > 1;
            }
            return true;
        } else {
            return !ChartHelper.chartZoomAndDataRangeEqual(chartZoom, chartRange.range);
        }
    }

    protected hasExportButton() {
        var chartHideExports =
            ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_HIDE_EXPORTS', CHART_HIDE_EXPORTS.DEFAULT));
        return this.isDefaultChart() || !chartHideExports;
    }

    protected getBaseConfig(chartEvents, xAxisEvents) {
        var showExportLabel = this.$chartContainer.width() > 700;
        var title = this.options.widget.getParamValue('CHART_TITLE', null);

        var defaultChartExportButtonPositionConfig = {
            contextButton: {
                text: showExportLabel ? 'Export' : '',
                align: 'right',
                theme: {
                    // Ensure export button is on top of any other element
                    zIndex: 10
                }
            }
        };
        var nonDefaultChartExportButtonPositionConfig = {
            contextButton: {
                text: showExportLabel ? 'Export' : '',
                align: 'left',
                theme: {
                    // Ensure export button is on top of any other element
                    zIndex: 10
                }
            }
        };

        // If this is a default chart, use the user timezone.
        let timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
        if(this.isDefaultChart()) timezone = this.options.currentUser.get('timezone');

        var config = {
            chart: {
                events: chartEvents,
                resetZoomButton: {
                    theme: { display: 'none' }
                },
                marginTop: this.isDefaultChart() ? 60 : 30,
                marginBottom: this.isDefaultChart() ? 20 : 30,


            },
            credits: { enabled: false },
            title: { text: null },
            xAxis: {
                type: 'datetime',
                title: null,
                events: xAxisEvents
            },
            exporting: {
                enabled: this.hasExportButton(),
                sourceWidth: 1200,
                sourceHeight: 600,
                chartOptions: {
                    legend: {
                        layout: 'vertical',
                        backgroundColor: 'white',
                        align: 'left',
                        verticalAlign: 'middle',
                        y: 0,
                        x: 0,
                        borderWidth: 0,
                        borderRadius: 0,
                        floating: false,
                        draggable: false,
                        zIndex: 20,
                        margin: 15,
                        enabled: true,
                        symbolHeight: null
                    }
                },
                csv: {
                    dateFormat: '%Y-%m-%d %H:%M:%S'
                  },
                buttons: this.isDefaultChart() ?
                    defaultChartExportButtonPositionConfig :
                    nonDefaultChartExportButtonPositionConfig
            },
            rangeSelector: {
                enabled: false,
            },
            scrollbar: {
                enabled: false,
            },
            navigation: {
                buttonOptions: {
                    theme: {
                        fill: '#f7f7f7',
                        r: 0,
                        states: {
                            hover: {
                                fill: '#e7e7e7',
                                'stroke-width': 0,
                                style: {
                                    cursor: 'pointer'
                                }
                            }
                        }
                    }
                }
            },
            loading: {
                style: {
                    position: 'absolute',
                    backgroundColor: 'white',
                    opacity: 0.5,
                    textAlign: 'center'
                }
            },
            legend: { enabled: false },
            series: [],
            time: {
                timezone: timezone
            }
        };

        if (title) {
            config.title = {
                text: title
            }
        }

        // Enable/Disable navigator
        if (this.hasNavigator()) {
            config['navigator'] = {
                series: {
                    color: '#7798BF',
                    lineColor: '#A6C7ED',
                    type: 'area',
                    marker: { enabled: false, states: { hover: { enabled: true } } },
                    connectNulls: false,
                    includeInCSVExport: false
                },
                xAxis: {
                    ordinal: false
                },
                height: Math.max(this.$chartContainer.height() / 10, 25)
            }
        } else {
            config['navigator'] = { enabled: false, height: 0 };
        }

        // Set export options for charts
        var exportButtonIsTiny = this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.DEFAULT) < 2;
        var exportButtonIsSmall = this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.DEFAULT) < 3;
        if (exportButtonIsTiny) {
            var highchartsMenuItems: string[] | MenuItem[] = Highcharts.getOptions().exporting.buttons.contextButton.menuItems;
            // Remove all except PNG & CSV options on 'tiny' widgets
            config.exporting.buttons.contextButton['menuItems'] = [highchartsMenuItems[2], highchartsMenuItems[6]];
        } else if (exportButtonIsSmall) {
            var highchartsMenuItems = Highcharts.getOptions().exporting.buttons.contextButton.menuItems;
            // Remove divider, JPEG, & SVG options from the exports button dropdown on 'small' widgets
            config.exporting.buttons.contextButton['menuItems'] = [
                highchartsMenuItems[0],
                highchartsMenuItems[2],
                highchartsMenuItems[4],
                highchartsMenuItems[6]
            ];
        }

        return <any>config;
    }

    protected handleEditWidgetClick() {
        this.options.widget.once('sync', () => {
            this.reset();

            this.options.dashboardChannelCollection.once('sync', () => {
                this.resetChartConfig();

                // Get widget channels and sort them by greatest timestamp
                const widgetChannels = this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widget.get('id') });
                const widgetChannelSortedByTimestamp = _.sortBy(widgetChannels, channel => {
                    const timestamp = channel.get('channel_last_timestamp');
                    return timestamp !== undefined && timestamp !== null ? -timestamp : -Infinity;
                });
                const widgetChannelsIDs = _.map(widgetChannelSortedByTimestamp, channel => channel.get('dashboard_channel_id'));

                this.resetAndLoadInitialPlot(Infinity, null, widgetChannelsIDs);
                this.requestSelectedChannels();
            });

            var chartSize = Number(this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.SIZE_6));
            var widgetEl = this.$el;
            if (this.options.widget.getParamValue('CHART_IS_TALL', CHART_IS_TALL.DISABLED) === CHART_IS_TALL.ENABLED) {
                widgetEl.addClass('tall');
            } else {
                widgetEl.removeClass('tall');
            }
            for (var i = 1; i <= 6; i++) {

                if (i === chartSize) {
                    widgetEl.addClass('widget-w-' + i);
                } else {
                    widgetEl.removeClass('widget-w-' + i);
                }
            }
        });
        this.options.manager.createModifyDashboardWidgetModal(this.options.widget);
    }

    protected setChartExportName(channelIds) {
        if (!this.chart) return;

        var names = _.map(channelIds, (id) => {
            // Use default name or custom name based on configuration
            const channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: id });
            let dashboardChannelName = channel.get('dashboard_channel_name');
            const moduleName = channel.get('module_location');
            if(this.isDefaultChart()){
                // Add module name if it's nbot already included in the name
                if (!dashboardChannelName.includes(moduleName)) {
                    dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`
                }
            } else if(!this.useCustomLabels){
                dashboardChannelName = `${moduleName} - ${channel.get('channel_name')}`;
            }
            return dashboardChannelName;
        });
        this.chart.options.exporting.filename = Utils.String.slugify(_.uniq(names).join('_')).trim().slice(0, 30);
    }

    protected getValueFromUIOrDefault(cssClass: string, attr: string, _default: string): string {
        var selector = this.$(cssClass);
        if (selector.length < 1) return _default;

        if (attr) {
            return String(selector.attr(attr)) !== '' ? this.$(cssClass).attr(attr) : _default
        } else {
            return selector.val() !== '' ? this.$(cssClass).val()[0] : _default
        }
    }

    /**
     * Gets a color from the Highcharts defaults colors array based on the order.
     * If the order exceeds the length of the array, it wraps around using the modulo operator.
     * 
     * @param {number} order - The order number to get the color for.
     * @return {string} The color corresponding to the given order.
     */
    getColorByOrder(order) {
        const highChartColors = this.chart.options.colors;
        const colorsLength = highChartColors.length;
        const index = order % colorsLength;
        return highChartColors[index];
    }

    remove() {
        // Free up the highcharts
        if (this.chart) {
            // When the chart does not have a series, or has not yet initialized highcharts will throw cryptic errors
            //
            // Even worse, if the chart is updating data when it is being destroyed, destroying the chart just fails
            // in this case, just remove the container
            try {
                this.chart.destroy();
            } catch (e) {
                if (this.chart && this.chart.renderTo) {
                    $(this.chart.renderTo).remove();
                }
            }
            this.chart = null;
        }

        // Terminate web workers and unload everything
        this.destroy();

        return super.remove();
    }

    reflow() {
        if (this.chart) this.chart.reflow();
    }

    // Planned
    addPlotBandsFromThresholds(channels: DashboardChannel[]) {
        // const timestamps: PlotBand[] = [];
        for (const dc of channels) {
            const axisKey = this.getAxisKeyForChannel(dc);
            const axis = this.chart.get(axisKey);
            const extremes = axis.getExtremes();

            const rangesList = dc.get('rangesList');
            for (const r of rangesList) {
                const range = new DashboardRange(r);
                if(range.get('is_default')) continue;
                
                axis.addPlotBand({ 
                    color: `${range.get('color')}1A`,
                    from: range.get('min') ?? extremes.min, 
                    to: range.get('max') ?? extremes.max,
                });
            }
        }
    }

    /**
     * Adds plotbands to a chart based on the operating hours of a given set of installations.
     * Plot bands are used to visually highlight certain time periods on the chart.
     * The function assumes all installations have the same operating hours. 
     * It calculates the plotbands for each day between the minimum and maximum data requested dates for the chart, 
     * taking into account the operating hours for weekdays, Saturdays, and Sundays.
     *
     * @param {Install[]} installs - An array of Install objects containing operating hours information.
     */
    addPlotBandsFromInstallsOpHours(installs: Install[]) {
        if (installs.length === 0 ) return;

        // If the aggregation period is more greater than an hour, don't show the bands
        let aggPeriod;
        if (this.isDefaultChart()) {
            aggPeriod = this.getValueFromUIOrDefault('.chart-period', null, CHART_AGGREGATION_PERIOD.DEFAULT);
        } else {
            aggPeriod = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT);
        }
        if (aggPeriod >= CHART_AGGREGATION_PERIOD.DAY && aggPeriod <= CHART_AGGREGATION_PERIOD.YEAR) return;

        
        // Remove all existing plotbands before updating them
        this.chart.update({
            xAxis: {
                plotBands: []
            },
        }, true, false, false);

        const haveSameInstallValues = (installs) => {
            // if there is only one install, return immediately
            if (installs.length <= 1) return true;
    
            const firstInstall = installs[0];
            const properties = [
                'property_hours_start_weekday',
                'property_hours_end_weekday',
                'property_hours_start_saturday',
                'property_hours_end_saturday',
                'property_hours_start_sunday',
                'property_hours_end_sunday'
            ];
    
                    // Compare each install's properties to the first install's properties
            for (let i = 1; i < installs.length; i++) {
                for (let property of properties) {
                    if (firstInstall.get(property) !== installs[i].get(property)) {
                        return false;
                    }
                }
            }
            
            return true;
        };
    
        // Return if installs do not have the same operating hours
        if (!haveSameInstallValues(installs)) return;
    
        const install = installs[0];
        const plotbands: PlotBand[] = [];
        const timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
        // Convert property hours to milliseconds
        const startWeekday = this.minutesToMs(install.get('property_hours_start_weekday'));
        const endWeekday = this.minutesToMs(install.get('property_hours_end_weekday'));
        const startSaturday = this.minutesToMs(install.get('property_hours_start_saturday'));
        const endSaturday = this.minutesToMs(install.get('property_hours_end_saturday'));
        const startSunday = this.minutesToMs(install.get('property_hours_start_sunday'));
        const endSunday = this.minutesToMs(install.get('property_hours_end_sunday'));
        const intitdate = this.minDataRequested;
        const endDate = this.maxDataRequested;
    
        // Initialize the current date and maximum date requested in the specified timezone
        let currentDate = moment.tz(this.minDataRequested * 1000, timezone);
        const maxDataRequestedDate = moment.tz(this.maxDataRequested * 1000, timezone);
        let previousEndTimestamp = null;
        
        // Loop through each day from the initial date to the maximum date requested
        while (currentDate <= maxDataRequestedDate) {
            let initialTime, endTime;
            const dayOfWeek = currentDate.day();
            
            // Determine the start and end times based on the day of the week
            if (dayOfWeek === 0) { // Sunday
                initialTime = startSunday;
                endTime = endSunday;
            } else if (dayOfWeek === 6) { // Saturday
                initialTime = startSaturday;
                endTime = endSaturday;
            } else { // Weekdays
                initialTime = startWeekday;
                endTime = endWeekday;
            }
    
            // Calculate the initial and end timestamps for the current day
            const initialTimestamp = currentDate.clone().startOf('day').add(initialTime, 'milliseconds');
            const endTimestamp = currentDate.clone().startOf('day').add(endTime, 'milliseconds');

            // Add plot bands based on the calculated timestamps
            if (previousEndTimestamp === null) {
                plotbands.push({
                    from: intitdate * 1000,
                    to: initialTimestamp.valueOf()
                });
            } else {
                plotbands.push({
                    from: previousEndTimestamp,
                    to: initialTimestamp.valueOf()
                });
            }
    
            previousEndTimestamp = endTimestamp.valueOf();
            currentDate.add(1, 'day');
        }
    
        // Add the final plot band up to the end date
        plotbands.push({
            from: previousEndTimestamp,
            to: endDate * 1000
        });
    
        // Update the chart with the new plot bands
        this.chart.update({
            xAxis: {
                plotBands: plotbands
            },
        }, true, false, false);
    }
    
    
    /**
     * Converts the given number of minutes since the beginning of the day 
     * to a timestamp in milliseconds. If the input is 
     * null, undefined, or not a number, it returns null.
     *
     * @param {number} minutes - The number of minutes since the beginning of the day.
     * @return {number} - Milliseconds with the specified time.
     */
    minutesToMs(minutes: number): number | null {
        // Validate the input to check if it's a valid number
        if (minutes === null || minutes === undefined || isNaN(minutes)) {
            return null; // Return null for invalid input
        }

        // Convert minutes to milliseconds
        return minutes * 60 * 1000;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/chart/ChartView.ts